'use client'

import type { FC } from 'react'
import { cx } from '@/app/lib/utils'
import { PureButton } from '@/app/components/atoms/PureButton'
import { IconGrid, IconRows } from './VistaToggleIcons'
import { DEFAULT_GROUP_LABEL, VISTA_OPTIONS } from './VistaToggle.constants'
import type { ViewMode, VistaToggleProps } from './VistaToggle.types'
import styles from './VistaToggle.module.scss'

/** Icono decorativo asociado a cada disposición. */
const ICONS: Record<ViewMode, FC> = {
  horizontal: IconRows,
  vertical: IconGrid,
}

/**
 * VistaToggle — alterna la disposición de las cards del listado entre
 * horizontal (lista) y vertical (cuadrícula). Forma parte del filter bar del
 * Page Intro (variante PLP del DS).
 *
 * Componente controlado y desacoplado: el estado vive fuera vía `value` +
 * `onChange`; no se acopla a ningún contexto.
 *
 * Accesibilidad: el contenedor agrupa las opciones con `role="group"` y una
 * etiqueta accesible. Cada botón es icon-only, por lo que expone su propio
 * `aria-label`; el botón activo se marca con `aria-pressed`. El foco visible lo
 * aporta el `focus-ring` del DS a través de `PureButton`.
 *
 * Client Component: gestiona el handler `onClick` de los botones.
 */
export function VistaToggle({
  value,
  onChange,
  ariaLabel = DEFAULT_GROUP_LABEL,
  inverse = false,
  className,
}: VistaToggleProps) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cx(styles.root, inverse && styles.inverse, className)}
    >
      {/* Etiqueta "Vista" visible (paridad con la referencia) — el grupo ya
          lleva su propio `aria-label`, así que esta se marca decorativa para
          no duplicar el anuncio en lectores de pantalla. */}
      <span className={styles.label} aria-hidden="true">
        Vista
      </span>
      <div className={styles.buttons}>
        {VISTA_OPTIONS.map((option) => {
          const Icon = ICONS[option.value]
          const isActive = option.value === value
          return (
            <PureButton
              key={option.value}
              size="s"
              icon={<Icon />}
              ariaLabel={option.ariaLabel}
              ariaPressed={isActive}
              onClick={() => onChange(option.value)}
              className={cx(styles.btn, isActive && styles.active)}
            />
          )
        })}
      </div>
    </div>
  )
}
